import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import { cn } from '../../../lib/utils';
import { SpecraBadge } from '../ensigns/badge';
import { SpecraButton } from '../ensigns/button';
import { SpecraSpinner } from '../ensigns/spinner';
import { SpecraText } from '../ensigns/typography';
import { SpecraCard } from '../lieutenants/card';
import { SpecraEmptyState } from '../lieutenants/empty-state';
/**
 * Specra frame around a third-party document editor iframe: status bar, banner
 * slot, and the loading / ready / error / reloading states. The host fills the
 * remaining height (iframe editors need an explicit-height container).
 */ export const SpecraDocumentSurface = /*#__PURE__*/ React.forwardRef(({ banner, children, className, errorMessage, hostTestId, label, mode = 'edit', onExitVersion, onReload, presence, reloadingMessage, state = 'ready', status, title, versionLabel, ...props }, ref)=>{
    const busy = state === 'loading' || state === 'reloading';
    return(// biome-ignore lint/a11y/useSemanticElements: the region is the Specra card itself (one DOM root, ref forwarded); a <section> wrapper would add a second frame.
    /*#__PURE__*/ _jsxs(SpecraCard, {
        "aria-busy": busy,
        "aria-label": label,
        className: cn('flex h-full min-h-0 flex-col overflow-hidden border-[color:var(--border-control)]', className),
        "data-mode": mode,
        "data-state": state,
        padding: "none",
        radius: "md",
        ref: ref,
        role: "region",
        ...props,
        children: [
            title || versionLabel || status || presence || mode === 'view' ? /*#__PURE__*/ _jsxs("div", {
                className: "flex shrink-0 items-center gap-[var(--space-3)] border-b border-border px-[var(--space-3-5)] py-[var(--space-1-5)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "div",
                        className: "min-w-0 flex-1 truncate",
                        size: "body-ui",
                        children: title
                    }),
                    versionLabel ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsxs(SpecraBadge, {
                                variant: "neutral",
                                children: [
                                    versionLabel,
                                    " · read-only"
                                ]
                            }),
                            onExitVersion ? /*#__PURE__*/ _jsx(SpecraButton, {
                                onClick: onExitVersion,
                                size: "xs",
                                variant: "secondary",
                                children: "Back to current"
                            }) : null
                        ]
                    }) : mode === 'view' ? /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: "neutral",
                        children: "Read-only"
                    }) : null,
                    presence,
                    status
                ]
            }) : null,
            banner ? /*#__PURE__*/ _jsx("div", {
                className: "shrink-0 border-b border-border px-[var(--space-3-5)] py-[var(--space-2)]",
                children: banner
            }) : null,
            /*#__PURE__*/ _jsxs("div", {
                className: "relative min-h-[var(--component-document-host-min-height)] flex-1",
                children: [
                    state === 'error' ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                        action: onReload ? /*#__PURE__*/ _jsx(SpecraButton, {
                            onClick: onReload,
                            size: "sm",
                            children: "Reload"
                        }) : undefined,
                        className: "h-full",
                        description: errorMessage ?? "The document editor didn't load.",
                        role: "alert",
                        title: "Document unavailable"
                    }) : /*#__PURE__*/ _jsx("div", {
                        className: "absolute inset-0 [&_iframe]:size-full [&_iframe]:border-0",
                        "data-testid": hostTestId,
                        children: children
                    }),
                    busy ? // biome-ignore lint/a11y/useSemanticElements: an overlay div announces progress politely; <output> is for form results.
                    /*#__PURE__*/ _jsxs("div", {
                        className: "absolute inset-0 flex flex-col items-center justify-center gap-[var(--space-2)] bg-[color-mix(in_srgb,var(--surface-card)_80%,transparent)]",
                        role: "status",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraSpinner, {
                                "aria-hidden": "true",
                                label: false
                            }),
                            /*#__PURE__*/ _jsx(SpecraText, {
                                size: "body-ui",
                                tone: "muted",
                                children: state === 'loading' ? 'Opening the document…' : reloadingMessage ?? 'Updating the document…'
                            })
                        ]
                    }) : null
                ]
            })
        ]
    }));
});
SpecraDocumentSurface.displayName = 'SpecraDocumentSurface';
